import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { TokenApiService } from 'src/app/core/services/token-api.service';
import { ToastService } from 'src/app/core/services/toast.service';
import {
  DeveloperApiToken, PermissionLevel, TokenUsageEntry,
} from 'src/app/core/domain/developer-token.model';
import { IonicModule } from '@ionic/angular';
import { NgClass, DatePipe } from '@angular/common';

@Component({
    selector: 'app-token-detail',
    templateUrl: './token-detail.page.html',
    styleUrls: ['./token-detail.page.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        IonicModule,
        NgClass,
        DatePipe,
        TranslatePipe,
    ],
})
export class TokenDetailPage implements OnInit {
  token: DeveloperApiToken | null = null;
  usage: TokenUsageEntry[] = [];

  isLoading = true;
  loadError = false;

  private id = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private api: TokenApiService,
    private toast: ToastService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.id = this.route.snapshot.paramMap.get('id') ?? '';
    this.load();
  }

  load(): void {
    this.isLoading = true;
    this.loadError = false;

    this.api.getById(this.id).subscribe({
      next: token => {
        this.token = token;
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.isLoading = false;
        this.loadError = true;
        this.cdr.markForCheck();
      },
    });

    this.api.usage(this.id).subscribe({
      next: res => {
        this.usage = res.items;
        this.cdr.markForCheck();
      },
      error: () => { /* usage history is a display convenience — a failed fetch doesn't block the page */ },
    });
  }

  revoke(): void {
    if (!this.token) return;
    if (!confirm(this.translate.instant('tokens.confirmRevoke', { name: this.token.name }))) return;

    this.api.revoke(this.token.id).subscribe({
      next: () => { this.toast.success(this.translate.instant('tokens.revokedToast')); this.load(); },
      error: () => this.toast.error(this.translate.instant('tokens.errors.revokeFailed')),
    });
  }

  async copyPrefix(prefix: string): Promise<void> {
    await navigator.clipboard.writeText(prefix);
    this.toast.success(this.translate.instant('common.copiedToClipboard'));
  }

  backToList(): void {
    this.router.navigateByUrl('/tokens');
  }

  levelColor(level: PermissionLevel): string {
    switch (level) {
      case 'Write': return 'success';
      case 'Read': return 'primary';
      default: return 'medium';
    }
  }

  statusColor(statusCode: number): string {
    if (statusCode >= 500) return 'danger';
    if (statusCode >= 400) return 'warning';
    if (statusCode >= 200) return 'success';
    return 'medium';
  }

  methodBadgeClass(method: string): string {
    switch (method.toUpperCase()) {
      case 'GET': return 'method-badge--get';
      case 'POST': return 'method-badge--post';
      case 'PATCH':
      case 'PUT': return 'method-badge--patch';
      case 'DELETE': return 'method-badge--delete';
      default: return 'method-badge--get';
    }
  }

  isRevoked(): boolean { return !!this.token?.revokedAt; }
  isExpired(): boolean { return !!this.token?.expiresAt && new Date(this.token.expiresAt) < new Date(); }
}
